import { type ChangeEvent, useId } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getGetMeQueryKey, useUpdateMyPreferences } from '../api/generated/identity/identity';
import { changeLanguage } from '../i18n';
import { SUPPORTED_LOCALES, isSupportedLocale, resolveLocale } from '../i18n/locales';
import styles from './LanguageSelector.module.css';

export interface LanguageSelectorProps {
  /**
   * Grava a escolha na conta. So faz sentido com sessao: sem ela, `PATCH
   * /v1/me/preferences` responderia 401.
   */
  readonly persist?: boolean;
}

export function LanguageSelector({ persist = false }: LanguageSelectorProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const queryClient = useQueryClient();
  const updatePreferences = useUpdateMyPreferences();

  const handleChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const next = event.target.value;
    if (!isSupportedLocale(next)) return;

    // A troca e imediata e nao espera a rede. A §9.4 pede troca instantanea, e
    // esperar o `PATCH` para so entao trocar seria troca com espera.
    void changeLanguage(next);
    if (!persist) return;

    updatePreferences.mutate(
      { data: { locale: next } },
      {
        // O TEXTO troca aqui; DATA, HORA e DINHEIRO nao saem daqui. Eles saem de
        // `me.locale`, e `me` fica no cache do react-query — que o `PATCH` nao
        // tocava. O resultado era uma tela meio traduzida pelo resto da sessao:
        // "Charges" ao lado de "R$ 1.234,56" e "15 de ago. de 2026", numa conta
        // que acabou de pedir ingles.
        //
        // Invalidar, e nao gravar a resposta: e a mesma escolha de
        // `features/terms/TermsPage.tsx`, e ela vale mais aqui porque a
        // preferencia tambem chega por outro caminho — a administracao edita o
        // idioma da pessoa em `StudentsPage`. Recusada a alternativa de ler o
        // idioma de `i18n.language` nas ~40 telas que formatam: sao ~40 pontos
        // de esquecimento, e o proximo campo formatado nasceria errado.
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        },
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      <label className={styles.label} htmlFor={id}>
        {t('language.selector')}
      </label>
      <select
        id={id}
        className={styles.select}
        value={resolveLocale(i18n.language)}
        onChange={handleChange}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {t(`language.${locale}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
